import { cva } from 'class-variance-authority';
import { motion } from 'motion/react';
import * as Sentry from '@sentry/react';
import {
  Flex,
  Text,
  IconButton,
  Dropdown,
  Tag,
  PageCard,
  MarkupPreview,
  space,
  iconWeight,
  Icon,
  useReducedMotion,
} from '@nula/core-ui';

import type { WorkspaceDoc } from '@/lib/sync/entity-types';
import { formatDate } from '@/utils/format-date';
import { SharedByChip } from '@/lib/sharing/components/shared-by-chip';
import { useGatedActions } from '@/lib/sharing/hooks/use-gated-actions';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import { ConfirmMenuItem } from '@/components/confirm-menu-item/confirm-menu-item';
import { displayTitle, kindIcon } from '@/lib/documents/doc-title';
import { useLiveRecording } from '@/lib/recordings/use-live-recording';
import { RecordingDot } from '@/components/recording-dot/recording-dot';
import {
  DocumentEmptyIllustration,
  CanvasEmptyIllustration,
} from '@/components/empty-illustrations';
import { hasPreviewContent } from './has-preview-content';
import styles from './document-card.module.css';

// ── CVA ──────────────────────────────────────────────────────────────────────

const listCard = cva(styles.listCard);

/**
 * The empty-state preview for a card with no content, illustrated per entity
 * kind. Document has a bespoke illustration; any other kind falls back to a
 * muted text label.
 */
function renderEmptyPreview(kind: WorkspaceDoc['kind']) {
  if (kind === 'document') {
    return (
      <Flex
        align="center"
        justify="center"
        className={styles.emptyPreview}
        data-testid="document-empty-illustration"
      >
        <DocumentEmptyIllustration className={styles.emptyIllustration} />
      </Flex>
    );
  }
  if (kind === 'canvas') {
    return (
      <Flex
        align="center"
        justify="center"
        className={styles.emptyPreview}
        data-testid="canvas-empty-illustration"
      >
        <CanvasEmptyIllustration className={styles.emptyIllustration} />
      </Flex>
    );
  }
  return (
    <Flex align="center" justify="center" className={styles.emptyPreview}>
      <Text size="xs" className={styles.emptyPreviewLabel}>
        Empty document
      </Text>
    </Flex>
  );
}

// ── Document card ────────────────────────────────────────────────────────────

export function DocumentCard({
  doc,
  onOpen,
  onArchive,
  extraMenuItems,
  listView,
  checkboxNode,
}: {
  doc: WorkspaceDoc;
  onOpen: (id: string) => void;
  onArchive: (id: string) => void;
  extraMenuItems?: Array<{ key: string; label: string; icon: React.ReactNode; onClick: () => void }>;
  listView?: boolean;
  checkboxNode?: React.ReactNode;
}) {
  const title = displayTitle(doc);
  const shouldReduce = useReducedMotion();
  const icon = kindIcon(doc.kind);
  const isRecording = useLiveRecording(doc.id)?.phase === 'capturing';
  const preview = doc.snippet || (doc.kind === 'canvas' ? 'A visual canvas' : 'Start writing...');
  const service = useWorkspaceService();

  // Gate destructive actions by the user's permission on this doc.
  const gatedDocActions = useGatedActions(doc.id, [
    { key: 'trash', require: 'delete' as const, label: 'Move to Trash', icon: <Icon name="delete" size={14} weight={iconWeight.light} />, danger: true, onClick: () => onArchive(doc.id) },
    {
      key: 'remove-self',
      require: 'removeSelf' as const,
      icon: <Icon name="logout" size={14} weight={iconWeight.light} />,
      onClick: () => {},
      label: (
        <ConfirmMenuItem
          label="Remove from my workspace"
          confirmLabel="Click to confirm"
          onConfirm={() => service.leaveEntity(doc.id).catch((err) => Sentry.captureException(err))}
        />
      ),
    },
  ]);

  if (listView) {
    return (
      <motion.div
        whileHover={shouldReduce ? {} : { y: -space[1] }}
        onClick={() => onOpen(doc.id)}
        className={listCard()}
      >
        <Flex
          align="center"
          gap={space[3]}
          className={styles.listCardInner}
        >
          {/* Type icon */}
          <Flex
            align="center"
            justify="center"
            className={styles.typeIconWrap}
          >
            <Icon
              name={icon}
              size={18}
              weight={iconWeight.light}
              className={styles.typeIcon}
            />
          </Flex>

          {/* Title + snippet + tags + shared-by chip */}
          <Flex vertical className={styles.titleCol}>
            <Flex align="center" gap={space[2]}>
              <Text
                ellipsis
                className={styles.titleText}
              >
                {title}
              </Text>
              {isRecording && <RecordingDot />}
            </Flex>
            <Text
              ellipsis
              className={styles.snippetText}
            >
              {preview}
            </Text>
            {doc.tags.length > 0 && (
              <Flex align="center" wrap gap={space[1]} className={styles.tagsWrapRow}>
                {doc.tags.slice(0, 3).map((tag) => (
                  <Tag key={tag} bordered={false} className={styles.tag}>
                    {tag}
                  </Tag>
                ))}
                {doc.tags.length > 3 && (
                  <Text size="xs" className={styles.tagsOverflow}>
                    +{doc.tags.length - 3}
                  </Text>
                )}
              </Flex>
            )}
            <SharedByChip access={doc.access} />
          </Flex>

          {/* Date */}
          <Text className={styles.dateText}>
            {formatDate(doc.updatedAt)}
          </Text>

          {/* More menu */}
          <Flex onClick={(e) => e.stopPropagation()} className={styles.moreBtn}>
            <Dropdown
              trigger={['click']}
              placement="bottom-end"
              menu={{
                items: [
                  { key: 'open', label: 'Open', icon: <Icon name="open_in_new" size={14} weight={iconWeight.light} />, onClick: () => onOpen(doc.id) },
                  ...(gatedDocActions.length > 0 ? [{ key: 'd1', label: '', type: 'divider' as const }] : []),
                  ...(extraMenuItems || []),
                  ...gatedDocActions,
                ],
              }}
            >
              <IconButton size="sm" aria-label="More options" className={styles.moreIconBtn}>
                <Icon name="more_horiz" size={16} weight={iconWeight.light} />
              </IconButton>
            </Dropdown>
          </Flex>
        </Flex>
      </motion.div>
    );
  }

  return (
    <PageCard
      preview={
        // Gate on whether the markup has VISIBLE content — an empty doc carries
        // a non-null but structurally-empty previewMarkup (e.g. "<p></p>") that
        // would otherwise render a blank MarkupPreview instead of the empty
        // illustration.
        hasPreviewContent(doc.previewMarkup) ? (
          <MarkupPreview key={doc.updatedAt} markup={doc.previewMarkup} />
        ) : (
          renderEmptyPreview(doc.kind)
        )
      }
      title={title}
      meta={formatDate(doc.updatedAt)}
      icon={
        isRecording ? (
          <Flex align="center" gap={space[1]}>
            <Icon name={icon} size={14} weight={iconWeight.light} />
            <RecordingDot />
          </Flex>
        ) : (
          <Icon
            name={icon}
            size={14}
            weight={iconWeight.light}
          />
        )
      }
      actions={
        <Dropdown
          trigger={['click']}
          placement="bottom-end"
          menu={{
            items: [
              { key: 'open', label: 'Open', icon: <Icon name="open_in_new" size={14} weight={iconWeight.light} />, onClick: () => onOpen(doc.id) },
              ...(gatedDocActions.length > 0 ? [{ key: 'd1', label: '', type: 'divider' as const }] : []),
              ...(extraMenuItems || []),
              ...gatedDocActions,
            ],
          }}
        >
          <IconButton size="sm" aria-label="More options">
            <Icon name="more_horiz" size={16} weight={iconWeight.light} />
          </IconButton>
        </Dropdown>
      }
      actionsLeft={checkboxNode}
      onClick={() => onOpen(doc.id)}
    />
  );
}
